import React, { memo, useState } from 'react'
import styled from 'styled-components'
import { useNavigate, useParams } from 'react-router-dom'

import { NfcTag } from '../../../common/redux/api/web-api'
import { WidgetLayout } from '../../../lib/widget-layout'
import { Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableBodyCell } from '../../../lib/table'
import Text from '../../../ui/Text'
import EditNFCGroupModal from '../../../ui/EditNFCGroupModal'
import { Button } from '../../../common/components/Button'
import useText from '../../../common/hooks/useText'
import { RfidTagModal } from '../../../common/components/rfid-tag-modal'

const RfidTable = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`

export type AdminUserRfidTagsWidgetProps = {
    nfcTagMessage?: string
    nfcTagError?: string
    nfcTags: NfcTag[]
    editNfcTagsGroup: (nfcTags: NfcTag[]) => void
    deleteNfcTagFromUser: (nfcTags: NfcTag) => void
    isAdmin: boolean
    style?: React.CSSProperties
}

export const AdminUserRfidTagsWidget = memo(function AdminUserRfidTagsWidget({
    nfcTags,
    isAdmin,
    editNfcTagsGroup,
    deleteNfcTagFromUser,
    style,
}: AdminUserRfidTagsWidgetProps): React.ReactElement {
    const text = useText()
    const navigate = useNavigate()
    const { id: userId } = useParams()
    const [showModal, setShowModal] = useState<boolean>(false)
    const [editTag, setEditTag] = useState<NfcTag | null>(null)

    const renderWidgetHeaderButtons = () => {
        return [
            isAdmin && nfcTags.length ? (
                <Button
                    label={text('btnEditGroup')}
                    size="sm"
                    className="margin-right-small"
                    onClick={() => setShowModal(true)}
                    data-testid="btn-edit"
                />
            ) : (
                <></>
            ),
            isAdmin ? (
                <Button
                    label={text('btnAdd')}
                    size="sm"
                    onClick={() => navigate(`/user/${userId}/userNfcTags`)}
                    data-testid="btn-add"
                />
            ) : (
                <></>
            ),
        ]
    }

    return (
        <div style={style}>
            <WidgetLayout
                title={text('lblRFIDWidget')}
                headerButtons={renderWidgetHeaderButtons()}
                data-testid="rfid-tags"
            >
                <Table data-testid="rfid-tags-table">
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell data-testid="lbl-id">
                                <Text code="lblId"></Text>
                            </TableHeaderCell>
                            <TableHeaderCell data-testid="lbl-comment">
                                <Text code="lblComment"></Text>
                            </TableHeaderCell>
                            <TableHeaderCell></TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {nfcTags?.map((nfctag) => (
                            <TableRow key={nfctag.id} data-testid={`row-${nfctag.id}`}>
                                <TableBodyCell>
                                    <RfidTable data-testid={`id-${nfctag.id}`}>
                                        {nfctag.id}
                                        <div>
                                            <Text code="lblExternalId" />:{' '}
                                            {nfctag.externalId ? (
                                                nfctag.externalId
                                            ) : (
                                                <Text code="msgValueNotSet"></Text>
                                            )}
                                        </div>
                                    </RfidTable>
                                </TableBodyCell>
                                <TableBodyCell data-testid={`commet-${nfctag.id}`}>{nfctag.comment}</TableBodyCell>
                                <TableBodyCell>
                                    {isAdmin && (
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => deleteNfcTagFromUser(nfctag)}
                                                className="btn btn-danger btn-xs pull-right btn-icon"
                                                data-testid="btn-delete"
                                            >
                                                <span data-testid="remove-icon" className="fa fa-remove"></span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setEditTag(nfctag)}
                                                className="btn btn-ok btn-xs pull-right btn-icon  margin-right-xsmall"
                                                data-testid="btn-edit"
                                            >
                                                <span data-testid="edit-icon" className="fa fa-edit"></span>
                                            </button>
                                        </div>
                                    )}
                                </TableBodyCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </WidgetLayout>
            {showModal && (
                <EditNFCGroupModal
                    nfcTags={nfcTags}
                    dismiss={() => setShowModal(false)}
                    close={(tags) => {
                        editNfcTagsGroup(tags)
                        setShowModal(false)
                    }}
                />
            )}
            {editTag ? <RfidTagModal rfidTag={editTag} onClose={() => setEditTag(null)} /> : null}
        </div>
    )
})
